import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight, ArrowDown, Building2, CheckCircle2, Calculator as CalcIcon,
  HardHat, Wind, KeyRound, Wrench, Briefcase, MapPin,
} from "lucide-react";
import { Reveal } from "@/components/od/Reveal";
import { MaskedLine } from "@/components/od/Hero";
import { INDUSTRIES, PEOPLE_BUCKETS, CAPACITY_QUESTIONS, CAPACITY_EXAMPLE, getIndustry, MIAMI_EN } from "@/data/miamiConfig";

const API = process.env.REACT_APP_BACKEND_URL;
const INDUSTRY_ICONS = { construction: HardHat, hvac: Wind, "property-management": KeyRound, "plumbing-field-service": Wrench, other: Briefcase };

function track(event, extra = {}) {
  try {
    fetch(`${API}/api/miami/events`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ event, ...extra }),
      keepalive: true,
    });
  } catch { /* analytics must never break the page */ }
}

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-sm text-brand-navy placeholder:text-slate-400 outline-none transition-colors duration-300 focus:border-brand-teal focus:ring-2 focus:ring-brand-teal/20";

export function MiamiFlow({ industries, buckets, questions, example, getIndustryFn, copy: t, source, market = "miami" }) {
  const [industryId, setIndustryId] = useState(() => localStorage.getItem("od-industry") || "");
  const industry = getIndustryFn(industryId);

  const [people, setPeople] = useState("");
  const [hours, setHours] = useState({});
  const [hourlyCost, setHourlyCost] = useState("");
  const [calcDone, setCalcDone] = useState(false);

  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", bottleneck: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const calcRef = useRef(null);
  const formRef = useRef(null);
  const flowRef = useRef(null);
  const tracked = useRef({});

  useEffect(() => {
    document.title = t.docTitle;
    // Approximate market check (coarse region only; never identifies a person or company)
    if (!sessionStorage.getItem("od-geo-tracked")) {
      axios.get(`${API}/api/geo`).then((r) => {
        track("visitor_location", { meta: r.data.scope || "unknown" });
        sessionStorage.setItem("od-geo-tracked", "1");
      }).catch(() => {});
    }
  }, [t.docTitle]);

  const selectIndustry = (id) => {
    setIndustryId(id);
    localStorage.setItem("od-industry", id);
    const ind = getIndustryFn(id);
    track("industry_selected", { industry: ind.label, process: ind.process, meta: source });
    track("process_selected", { industry: ind.label, process: ind.process, meta: source });
    setForm((f) => ({ ...f, bottleneck: "" }));
  };

  const scrollTo = (ref) => ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const weeklyTotal = useMemo(
    () => Object.values(hours).reduce((a, b) => a + (parseFloat(b) || 0), 0),
    [hours]
  );
  const annualTotal = weeklyTotal * 52;
  const annualValue = hourlyCost ? annualTotal * (parseFloat(hourlyCost) || 0) : null;

  const onHoursChange = (id, value) => {
    if (!tracked.current.calcStarted) {
      tracked.current.calcStarted = true;
      track("calculator_started", {
        ...(industry ? { industry: industry.label, process: industry.process } : {}),
        meta: source,
      });
    }
    setHours((h) => ({ ...h, [id]: value }));
    setCalcDone(false);
  };

  const runCalculation = () => {
    if (!people || weeklyTotal <= 0) {
      toast.error(t.toastCalcError);
      return;
    }
    setCalcDone(true);
    track("calculator_completed", {
      ...(industry ? { industry: industry.label, process: industry.process } : {}),
      weekly_hours: weeklyTotal,
      meta: `${source} people:${people}`,
    });
  };

  const canSubmit = form.name.trim() && /\S+@\S+\.\S+/.test(form.email) && industryId && form.bottleneck;

  const submitLead = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSending(true);
    try {
      await axios.post(`${API}/api/leads`, {
        name: form.name,
        email: form.email,
        company: form.company,
        phone: form.phone,
        industry: industry.label,
        bottleneck: form.bottleneck,
        time_drain: form.bottleneck,
        people_range: people,
        weekly_hours: weeklyTotal || null,
        annual_hours: weeklyTotal ? annualTotal : null,
        hourly_cost: hourlyCost ? parseFloat(hourlyCost) : null,
        est_annual_value: annualValue,
        market,
        source,
      });
      track("lead_submitted", { industry: industry.label, process: industry.process, weekly_hours: weeklyTotal || undefined, meta: source });
      setDone(true);
      toast.success(t.toastSuccess);
    } catch {
      toast.error(t.toastError);
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="pt-16 sm:pt-20 bg-[#F8FAFC]">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Ocean Data", item: "https://oceandata.ca" },
              { "@type": "ListItem", position: 2, name: t.eyebrow.replace("OceanData · ", ""), item: `https://oceandata.ca${source}` },
            ],
          }),
        }}
      />
      {/* 1 — Hero */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <p className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-6">
            <MapPin size={13} /> {t.eyebrow}
          </p>
          <h1
            data-testid="miami-headline"
            className="font-display font-bold tracking-tight leading-[1.06] text-brand-navy text-4xl sm:text-5xl lg:text-6xl max-w-3xl"
          >
            <MaskedLine delay={0.15} testId="miami-headline-line-1">{t.heroLine1}</MaskedLine>
            <MaskedLine delay={0.3} testId="miami-headline-line-2">
              <span className="text-brand-teal">{t.heroLine2}</span>
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="mt-7 max-w-xl text-base sm:text-lg text-slate-600 leading-relaxed"
          >
            {t.heroSub}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mt-9 flex flex-col sm:flex-row sm:items-center gap-4"
          >
            <button
              data-testid="miami-cta-bottleneck"
              onClick={() => { track("cta_clicked", { meta: `${source} find-my-bottleneck` }); scrollTo(flowRef); }}
              className="inline-flex items-center justify-center gap-3 rounded-full bg-brand-navy text-white text-base font-semibold px-9 py-4 transition-all duration-300 hover:bg-brand-teal hover:-translate-y-0.5"
            >
              {t.ctaBottleneck} <ArrowDown size={18} />
            </button>
            <button
              data-testid="miami-cta-calculate"
              onClick={() => { track("cta_clicked", { meta: `${source} calculate-my-capacity` }); scrollTo(calcRef); }}
              className="inline-flex items-center justify-center gap-3 rounded-full border-2 border-brand-navy text-brand-navy text-base font-semibold px-9 py-[14px] transition-all duration-300 hover:border-brand-teal hover:text-brand-teal"
            >
              {t.ctaCalculate}
            </button>
          </motion.div>
        </div>
      </section>

      {/* 2 — Industry selector */}
      <section className="py-14 lg:py-20 bg-white border-y border-slate-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-3">
            {t.stepOneEyebrow}
          </p>
          <h2 className="font-display font-bold tracking-tight text-brand-navy text-3xl sm:text-4xl mb-3">
            {t.selectorTitle}
          </h2>
          <p className="text-base text-slate-600 mb-10">{t.selectorSub}</p>
          <div data-testid="industry-selector" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {industries.map((ind, i) => {
              const Icon = INDUSTRY_ICONS[ind.id];
              const active = industryId === ind.id;
              return (
                <Reveal key={ind.id} delay={i * 0.05}>
                  <button
                    data-testid={`industry-${ind.id}`}
                    onClick={() => selectIndustry(ind.id)}
                    aria-pressed={active}
                    className={`w-full h-full rounded-2xl border-2 p-5 sm:p-6 text-left transition-all duration-300 hover:-translate-y-0.5 ${
                      active
                        ? "border-brand-teal bg-brand-tealsoft"
                        : "border-slate-200 bg-[#F8FAFC] hover:border-brand-teal/60"
                    }`}
                  >
                    <span className={`w-10 h-10 rounded-full flex items-center justify-center mb-4 ${active ? "bg-brand-teal text-white" : "bg-white text-brand-teal border border-slate-200"}`}>
                      <Icon size={17} />
                    </span>
                    <p className="font-display font-semibold text-brand-navy text-base leading-snug">{ind.label}</p>
                    <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-slate-400 mt-1.5">{ind.process}</p>
                  </button>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* 3 — Industry flow */}
      <section ref={flowRef} className="py-16 lg:py-24 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <AnimatePresence mode="wait">
            {industry ? (
              <motion.div
                key={industry.id}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.4 }}
              >
                <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-3">
                  {industry.label} · {industry.process}
                </p>
                <h2 data-testid="flow-headline" className="font-display font-bold tracking-tight text-brand-navy text-3xl sm:text-4xl max-w-2xl mb-4">
                  {industry.headline}
                </h2>
                <p className="max-w-2xl text-base sm:text-lg text-slate-600 leading-relaxed mb-10">
                  {industry.message}
                </p>

                {/* Role chain */}
                <div data-testid="process-flow" className="rounded-3xl bg-white border border-slate-200 p-6 sm:p-8 mb-6 overflow-x-auto">
                  <div className="flex items-stretch gap-2 min-w-max">
                    {industry.roles.map((role, i) => (
                      <div key={role} className="flex items-center gap-2">
                        <div
                          data-testid={`flow-role-${i}`}
                          className="rounded-xl bg-brand-tealsoft border border-brand-tealborder px-4 py-3 text-center"
                        >
                          <p className="font-display font-semibold text-brand-navy text-sm whitespace-nowrap">{role}</p>
                        </div>
                        {i < industry.roles.length - 1 && (
                          <ArrowRight size={15} className="text-brand-teal shrink-0" />
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 pt-6 border-t border-slate-100">
                    <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-slate-400 font-semibold mb-3">
                      {t.oneRecordCarries}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {industry.info.map((item) => (
                        <span key={item} className="rounded-full bg-[#F8FAFC] border border-slate-200 text-slate-600 text-xs font-medium px-3.5 py-1.5">
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center gap-5">
                  <button
                    data-testid="flow-cta"
                    onClick={() => { track("cta_clicked", { industry: industry.label, process: industry.process, meta: `${source} industry-find-bottleneck` }); scrollTo(formRef); }}
                    className="inline-flex items-center justify-center gap-3 rounded-full bg-brand-navy text-white text-base font-semibold px-9 py-4 transition-all duration-300 hover:bg-brand-teal hover:-translate-y-0.5"
                  >
                    {industry.cta} <ArrowRight size={18} />
                  </button>
                  <p className="text-sm text-slate-500">{t.flowCtaHint}</p>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="empty"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="rounded-3xl border-2 border-dashed border-slate-200 bg-white p-10 sm:p-14 text-center"
              >
                <Building2 size={22} className="text-brand-teal mx-auto mb-4" />
                <p data-testid="flow-empty" className="font-display font-medium text-xl text-brand-navy max-w-md mx-auto leading-snug">
                  {t.flowEmpty}
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* 4 — How OceanData works */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-8">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-3">
            {t.howEyebrow}
          </p>
          <h2 className="font-display font-bold tracking-tight text-brand-navy text-3xl sm:text-4xl mb-12">
            {t.howTitle}
          </h2>
          <div className="space-y-0">
            {t.steps.map((step, i) => (
              <Reveal key={step} delay={i * 0.06}>
                <div data-testid={`miami-step-${i}`}>
                  <div className="flex items-center gap-5 rounded-2xl bg-[#F8FAFC] border border-slate-200 px-6 py-5">
                    <span className="font-display font-bold text-2xl text-brand-teal w-8 shrink-0">{i + 1}</span>
                    <p className="font-display font-semibold text-lg text-brand-navy">{step}</p>
                  </div>
                  {i < t.steps.length - 1 && (
                    <div className="flex justify-center py-1.5" aria-hidden="true">
                      <ArrowDown size={15} className="text-brand-teal/50" />
                    </div>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 5 — Capacity example */}
      <section className="py-16 lg:py-24 bg-brand-navy text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-gold font-semibold mb-3">
            {t.roiEyebrow}
          </p>
          <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl max-w-2xl mb-12">
            {t.roiTitle}
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
            {example.map((r, i) => (
              <Reveal key={r.role} delay={i * 0.06}>
                <div data-testid={`capacity-example-${i}`} className="rounded-2xl bg-brand-navysurf border border-white/10 p-6">
                  <p className="font-display font-bold text-3xl text-white mb-1">+{r.hours} hrs</p>
                  <p className="text-sm text-brand-pale">{r.role} · {t.perWeek}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="flex flex-col sm:flex-row sm:items-center gap-6">
              <p className="font-display font-semibold text-2xl text-brand-gold">
                {t.roiTotal}
              </p>
              <button
                data-testid="capacity-cta"
                onClick={() => { track("cta_clicked", { meta: `${source} calculate-your-capacity` }); scrollTo(calcRef); }}
                className="inline-flex items-center gap-3 rounded-full bg-brand-teal text-white text-base font-semibold px-8 py-4 transition-all duration-300 hover:bg-brand-tealhover hover:-translate-y-0.5"
              >
                {t.roiCta} <CalcIcon size={17} />
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 6 — Capacity calculator */}
      <section ref={calcRef} className="py-16 lg:py-24 scroll-mt-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-8">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-3">
            {t.calcEyebrow}
          </p>
          <h2 className="font-display font-bold tracking-tight text-brand-navy text-3xl sm:text-4xl mb-4">
            {t.calcTitle}
          </h2>
          <p className="text-base text-slate-600 leading-relaxed mb-10">
            {t.calcSub}
          </p>

          <div data-testid="capacity-calculator" className="rounded-3xl bg-white border border-slate-200 p-7 sm:p-9">
            <p className="font-display font-semibold text-lg text-brand-navy mb-4">
              {t.peopleQ}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-9">
              {buckets.map((b) => (
                <button
                  key={b}
                  data-testid={`people-${b}`}
                  onClick={() => setPeople(b)}
                  aria-pressed={people === b}
                  className={`rounded-xl border-2 px-4 py-3.5 font-display font-semibold text-base transition-all duration-300 ${
                    people === b
                      ? "border-brand-teal bg-brand-tealsoft text-brand-navy"
                      : "border-slate-200 text-slate-600 hover:border-brand-teal/60"
                  }`}
                >
                  {b}
                </button>
              ))}
            </div>

            <p className="font-display font-semibold text-lg text-brand-navy mb-1">
              {t.hoursQ}
            </p>
            <p className="text-xs text-slate-400 mb-5">{t.hoursHint}</p>
            <div className="space-y-4 mb-9">
              {questions.map((q) => (
                <div key={q.id} className="flex items-center gap-4">
                  <label htmlFor={`calc-${q.id}`} className="flex-1 text-sm sm:text-base text-slate-700">
                    {q.label}
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      id={`calc-${q.id}`}
                      data-testid={`calc-${q.id}`}
                      type="number"
                      min="0"
                      max="200"
                      placeholder="0"
                      value={hours[q.id] || ""}
                      onChange={(e) => onHoursChange(q.id, e.target.value)}
                      className="w-24 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-brand-navy text-right outline-none transition-colors duration-300 focus:border-brand-teal focus:ring-2 focus:ring-brand-teal/20"
                    />
                    <span className="text-xs text-slate-400 w-6">hrs</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center gap-4 mb-9">
              <label htmlFor="calc-rate" className="flex-1 text-sm sm:text-base text-slate-700">
                {t.rateLabel}
              </label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-400">$</span>
                <input
                  id="calc-rate"
                  data-testid="calc-rate"
                  type="number"
                  min="0"
                  placeholder="65"
                  value={hourlyCost}
                  onChange={(e) => setHourlyCost(e.target.value)}
                  className="w-24 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-brand-navy text-right outline-none transition-colors duration-300 focus:border-brand-teal focus:ring-2 focus:ring-brand-teal/20"
                />
              </div>
            </div>

            <button
              data-testid="calc-run"
              onClick={runCalculation}
              className="w-full inline-flex items-center justify-center gap-3 rounded-full bg-brand-navy text-white text-base font-semibold px-8 py-4 transition-all duration-300 hover:bg-brand-teal"
            >
              {t.calcRun} <ArrowRight size={18} />
            </button>

            <AnimatePresence>
              {calcDone && weeklyTotal > 0 && (
                <motion.div
                  data-testid="calc-result"
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                  className="mt-8 rounded-2xl bg-brand-navy text-white p-7 sm:p-8"
                >
                  <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-brand-gold font-semibold mb-5">
                    {t.resultEyebrow}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-5">
                    <div>
                      <p data-testid="calc-weekly" className="font-display font-bold text-4xl text-white">
                        {weeklyTotal % 1 ? weeklyTotal.toFixed(1) : weeklyTotal} hrs
                      </p>
                      <p className="text-sm text-brand-pale mt-1">{t.perWeek}</p>
                    </div>
                    <div>
                      <p data-testid="calc-annual" className="font-display font-bold text-4xl text-white">
                        {Math.round(annualTotal).toLocaleString()} hrs
                      </p>
                      <p className="text-sm text-brand-pale mt-1">{t.perYear}</p>
                    </div>
                  </div>
                  {annualValue !== null && annualValue > 0 && (
                    <p data-testid="calc-value" className="font-display font-semibold text-xl text-brand-gold mb-4">
                      ≈ ${Math.round(annualValue).toLocaleString()} {t.valueSuffix}
                    </p>
                  )}
                  <p className="text-xs text-slate-400 leading-relaxed border-t border-white/10 pt-4">
                    {t.disclaimer}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* 7 — Lead form */}
      <section ref={formRef} className="py-16 lg:py-24 bg-white scroll-mt-20">
        <div className="max-w-xl mx-auto px-4 sm:px-8">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-3">
            {t.formEyebrow}
          </p>
          <h2 className="font-display font-bold tracking-tight text-brand-navy text-3xl sm:text-4xl mb-4">
            {t.formTitle}
          </h2>
          <p className="text-base text-slate-600 leading-relaxed mb-10">
            {t.formSub}
          </p>

          {done ? (
            <div data-testid="miami-lead-success" className="rounded-3xl bg-brand-tealsoft border border-brand-tealborder p-9 text-center">
              <CheckCircle2 size={28} className="text-brand-teal mx-auto mb-4" />
              <p className="font-display font-semibold text-xl text-brand-navy mb-2">{t.successTitle}</p>
              <p className="text-sm text-slate-600 leading-relaxed">
                {t.successBody}
              </p>
            </div>
          ) : (
            <form
              data-testid="miami-lead-form"
              onSubmit={submitLead}
              onFocusCapture={() => {
                if (!tracked.current.formStarted) {
                  tracked.current.formStarted = true;
                  track("lead_form_started", { ...(industry ? { industry: industry.label } : {}), meta: source });
                }
              }}
              className="rounded-3xl bg-[#F8FAFC] border border-slate-200 p-7 sm:p-9 space-y-4"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <select
                  data-testid="lead-industry"
                  value={industryId}
                  onChange={(e) => selectIndustry(e.target.value)}
                  className={inputCls}
                  required
                >
                  <option value="" disabled>{t.industryPh}</option>
                  {industries.map((i) => (
                    <option key={i.id} value={i.id}>{i.label}</option>
                  ))}
                </select>
                <select
                  data-testid="lead-bottleneck"
                  value={form.bottleneck}
                  onChange={(e) => setForm({ ...form, bottleneck: e.target.value })}
                  className={inputCls}
                  required
                >
                  <option value="" disabled>{t.bottleneckPh}</option>
                  {(industry ? industry.pains : [t.fallbackPain]).map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <select
                  data-testid="lead-people"
                  value={people}
                  onChange={(e) => setPeople(e.target.value)}
                  className={inputCls}
                >
                  <option value="" disabled>{t.peoplePh}</option>
                  {buckets.map((b) => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </select>
                <input
                  data-testid="lead-weekly-hours"
                  type="number"
                  min="0"
                  placeholder={t.weeklyHoursPh}
                  value={weeklyTotal || ""}
                  onChange={(e) => setHours({ total: e.target.value })}
                  className={inputCls}
                />
              </div>
              <input
                data-testid="lead-name"
                placeholder={t.namePh}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className={inputCls}
                required
              />
              <input
                data-testid="lead-company"
                placeholder={t.companyPh}
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                className={inputCls}
              />
              <input
                data-testid="lead-email"
                type="email"
                placeholder={t.emailPh}
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className={inputCls}
                required
              />
              <input
                data-testid="lead-phone"
                type="tel"
                placeholder={t.phonePh}
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className={inputCls}
              />
              <button
                data-testid="lead-submit"
                type="submit"
                disabled={!canSubmit || sending}
                className="w-full inline-flex items-center justify-center gap-3 rounded-full bg-brand-navy text-white text-base font-semibold px-8 py-4 transition-all duration-300 hover:bg-brand-teal disabled:opacity-50"
              >
                {sending ? t.submitSending : t.submitIdle}
                {!sending && <ArrowRight size={18} />}
              </button>
              <p className="text-xs text-slate-400 text-center">
                {t.privacy}
              </p>
            </form>
          )}
        </div>
      </section>

      {/* 8 — Final CTA */}
      <section className="bg-brand-navy text-white py-16 lg:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-8 text-center">
          <Reveal>
            <h2 className="font-display font-bold tracking-tight leading-[1.12] text-3xl sm:text-4xl mb-5">
              {t.finalTitle}
            </h2>
            <p className="text-base text-slate-300 leading-relaxed max-w-xl mx-auto mb-9">
              {t.finalSub}
            </p>
            <button
              data-testid="miami-final-cta"
              onClick={() => { track("cta_clicked", { meta: `${source} final-find-out` }); scrollTo(formRef); }}
              className="inline-flex items-center gap-3 rounded-full bg-brand-teal text-white text-base font-semibold px-10 py-4 transition-all duration-300 hover:bg-brand-tealhover hover:-translate-y-0.5"
            >
              {t.finalCta} <ArrowRight size={18} />
            </button>
            {t.relatedLinks && (
              <div className="mt-12">
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400 font-semibold mb-4">{t.relatedLabel}</p>
                <div className="flex flex-wrap justify-center gap-2.5">
                  {t.relatedLinks.map((l) => (
                    <a
                      key={l.href}
                      href={l.href}
                      className="rounded-full border border-white/20 text-slate-300 text-xs font-medium px-4 py-2 transition-colors duration-300 hover:border-brand-teal hover:text-white"
                    >
                      {l.label}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </Reveal>
        </div>
      </section>
    </main>
  );
}

export default function MiamiPage() {
  return (
    <MiamiFlow
      industries={INDUSTRIES}
      buckets={PEOPLE_BUCKETS}
      questions={CAPACITY_QUESTIONS}
      example={CAPACITY_EXAMPLE}
      getIndustryFn={getIndustry}
      copy={MIAMI_EN}
      source="/miami"
    />
  );
}
